import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Row,
  Col,
  Modal,
  Spin,
  Alert,
  Empty,
  Image,
  message,
} from 'antd';
import {
  ClockCircleOutlined,
  AppstoreOutlined,
  HomeOutlined,
  RiseOutlined,
  PlayCircleFilled,
} from '@ant-design/icons';
import moment from 'moment';
import CalendarHeatmap from '@/components/CalendarHeatmap';
import {
  CLOCK_TYPES,
  getSignOverview,
  getSignDailyDetail,
  toHeatmapWeeks,
  toSignDetailViews,
  hasUsableTrainingSplit,
  getSignBreakdown,
  dashIfNull,
} from '@/services/memberUserSignStat';

/**
 * 【S端】用户档案 · 近52周打卡概览（真实接口版）
 *
 * 与 CheckInOverviewDemo.jsx 的区别：数据来自 MemberUserSignStatApi，
 * 并且把「查询失败」和「这个用户没打卡」画成两种样子——
 * 后者是一片空白格子，容易被误读成"这人躺着不动"，前者必须显式报错并可重试。
 *
 * 概览卡：后端已拆真人/入镜/其它（trainingLiveCount / trainingMirrorCount / trainingOtherCount）。
 * 拆分不可信时（trainingSplitAvailable=false 或三档相加不等于总数）退回只显合并总数，
 * 判据统一用 hasUsableTrainingSplit，页面不自己采字段。
 *
 * 降级：到店取数失败不再整页报错，后端只置 offlineAvailable=false，
 * 页面给一条不阻断的提示，其余四类照常展示。
 */

/**
 * 训练拆分重试的间隔：给后端逐日补查留一点跑完 + 写缓存的时间。
 * 太短拿不到新缓存，太长首屏等得明显，600ms 是实测手感位。
 */
const SPLIT_RETRY_DELAY_MS = 600;

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// types 只把展示需要的三项交给热力图组件，field 是服务层适配用的
const HEATMAP_TYPES = CLOCK_TYPES.map(({ key, label, color }) => ({
  key,
  label,
  color,
}));

const CATEGORY_COLOR = {
  training: '#fa8c16',
  sport: '#52c41a',
  offline: '#722ed1',
  diet: '#1890ff',
  other: '#eb2f96',
};

// 故障文案：reason → 给用户看的一句话
const FAILURE_COPY = {
  BAD_REQUEST: '查询参数有误（日期需为 yyyy-MM-dd 且补零）',
  UPSTREAM_ERROR: '打卡数据服务暂不可用，请稍后重试',
  NETWORK_ERROR: '网络异常，打卡数据加载失败',
  MALFORMED_RESPONSE: '返回数据格式异常',
};

const IconBadge = ({ children, color = '#52c41a', bg = '#f6ffed' }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 40,
      height: 40,
      borderRadius: '50%',
      background: bg,
      color,
      fontSize: 18,
      flexShrink: 0,
    }}
  >
    {children}
  </span>
);

IconBadge.propTypes = {
  children: PropTypes.node,
  color: PropTypes.string,
  bg: PropTypes.string,
};

IconBadge.defaultProps = {
  children: null,
  color: '#52c41a',
  bg: '#f6ffed',
};

const StatCard = ({ icon, main, sub, big }) => (
  <Card
    bordered={false}
    style={{ height: '100%', background: '#fafafa' }}
    bodyStyle={{ padding: '20px 24px' }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      {icon}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: big || 22,
            fontWeight: 600,
            lineHeight: 1.3,
            color: 'rgba(0,0,0,.85)',
            whiteSpace: 'nowrap',
          }}
        >
          {main}
        </div>
        <div style={{ fontSize: 13, color: '#8c8c8c', marginTop: 2 }}>
          {sub}
        </div>
      </div>
    </div>
  </Card>
);

StatCard.propTypes = {
  icon: PropTypes.node,
  main: PropTypes.node,
  sub: PropTypes.node,
  big: PropTypes.number,
};

/** 明细弹窗内容：按分类分组渲染，组内逐条。 */
/**
 * 单日明细分组（按设计稿口径）：
 *   组标题  「4次到店打卡」（稿：次数在前）
 *   每行    17:09 ● 跑步机（用时31分钟26秒）  乐刻门店236
 *           时间左列 + 时间轴圆点（取分类色，与热力图图例一致）+ 标题（括号补充）
 *   饮食/自由打卡：标题下方先图后文字（PRD：排序 图片、文字）
 */
const RAIL_WIDTH = 14;
const TIME_WIDTH = 46;

/**
 * 心情图标：底层 icon 给的是图片 URL（C 端也是用 <image> 渲染），
 * 直接拼进文案就是一长串地址，所以当小图内联在括号里（对齐稿子「😐平静」）。
 */
const MoodIcon = ({ src }) => (
  <img
    src={src}
    alt=""
    style={{
      width: 16,
      height: 16,
      marginRight: 2,
      verticalAlign: '-3px',
      borderRadius: '50%',
    }}
  />
);

MoodIcon.propTypes = {
  src: PropTypes.string.isRequired,
};

/**
 * 视频缩略块：底层把图片/视频混在同一个 `image` 字段里（实测自由打卡有云点播 mp4 直链），
 * 用 video 首帧当封面（加 #t=0.1 让浏览器稳定取到画面）+ 播放角标；取不到帧给灰底「视频」占位。
 */
/**
 * 缩略块统一尺度。图片与视频必须共用同一个外层盒子：
 * antd Image 会把 style 落到内层 <img>，而外层 .ant-image 盒子尺寸被 width/height props 钉死，
 * 所以 margin / 圆角写在 Image 的 style 上会溢出到盒子外面：图与图实际零间距、图还会压住旁边的视频，
 * hover 遮罩也因为外层没圆角而露方角。尺寸 / 间距 / 圆角一律由外层盒子说了算。
 */
const MEDIA_SIZE = 72;
const MEDIA_RADIUS = 4;
const MEDIA_GAP = 8;

/** 图 / 视频共用的缩略块外壳 */
const MediaBox = ({ children }) => (
  <div
    style={{
      position: 'relative',
      display: 'flex', // 不走基线对齐，否则内层 inline-block 图会被推下几像素、底部被裁
      width: MEDIA_SIZE,
      height: MEDIA_SIZE,
      borderRadius: MEDIA_RADIUS,
      overflow: 'hidden',
      flex: '0 0 auto',
      background: '#f5f5f5',
    }}
  >
    {children}
  </div>
);

MediaBox.propTypes = {
  children: PropTypes.node.isRequired,
};

const VideoTile = ({ src, cover, onPlay }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div
      onClick={() => onPlay(src)}
      title="点击播放视频"
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        cursor: 'pointer',
        background: '#f5f5f5',
      }}
    >
      {failed ? (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            color: '#8c8c8c',
          }}
        >
          视频
        </div>
      ) : (
        <video
          src={cover ? undefined : `${src}#t=0.1`}
          poster={cover || undefined}
          muted
          preload="metadata"
          playsInline
          onError={() => setFailed(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      )}
      <PlayCircleFilled
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: 22,
          color: 'rgba(255,255,255,.92)',
          textShadow: '0 1px 6px rgba(0,0,0,.5)',
        }}
      />
    </div>
  );
};

VideoTile.propTypes = {
  src: PropTypes.string.isRequired,
  /** 后端给的封面图，有封面就不提前加载视频元信息 */
  cover: PropTypes.string,
  onPlay: PropTypes.func.isRequired,
};
VideoTile.defaultProps = { cover: null };

const DetailGroups = ({ groups }) => {
  /** 弹窗里嵌一个视频播放器：点缩略块时记住地址（destroyOnClose 保证关闭就停） */
  const [playing, setPlaying] = useState(null);
  if (!groups || !groups.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="当天没有打卡记录"
      />
    );
  }
  return (
    <div>
      {groups.map((g) => {
        const color = CATEGORY_COLOR[g.category] || '#722ed1';
        const last = g.items.length - 1;
        return (
          <div key={g.category} style={{ marginBottom: 18 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: 'rgba(0,0,0,.85)',
                marginBottom: 10,
              }}
            >
              {g.count}次{g.categoryLabel}
            </div>
            {g.items.map((it, idx) => (
              <div
                key={`${g.category}-${it.id != null ? it.id : idx}`}
                style={{ display: 'flex', alignItems: 'stretch' }}
              >
                <div
                  style={{
                    width: TIME_WIDTH,
                    flexShrink: 0,
                    fontSize: 13,
                    color: 'rgba(0,0,0,.65)',
                    lineHeight: '22px',
                  }}
                >
                  {it.timeText}
                </div>
                {/* 时间轴：圆点 + 连线（连线多拖 12px 接到下一个圆点中心，最后一行不画线） */}
                <div
                  style={{
                    width: RAIL_WIDTH,
                    flexShrink: 0,
                    position: 'relative',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      left: 4,
                      top: 9,
                      width: 6,
                      height: 6,
                      zIndex: 9,
                      borderRadius: '50%',
                      background: color,
                    }}
                  />
                  {idx !== last && (
                    <span
                      style={{
                        position: 'absolute',
                        left: 6,
                        top: 10,
                        bottom: -12,
                        width: 1,
                        background: color,
                      }}
                    />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0, paddingBottom: 12 }}>
                  <div style={{ fontSize: 13, lineHeight: '22px' }}>
                    <span style={{ color: 'rgba(0,0,0,.85)' }}>{it.title}</span>
                    {(it.paren || it.iconUrl) && (
                      <span style={{ color: 'rgba(0,0,0,.45)' }}>
                        （{it.iconUrl ? <MoodIcon src={it.iconUrl} /> : null}
                        {it.paren}）
                      </span>
                    )}
                    {it.merchantText && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 12,
                          color: '#8c8c8c',
                        }}
                      >
                        {it.merchantText}
                      </span>
                    )}
                  </div>
                  {(it.media || []).length > 0 && (
                    <div
                      style={{
                        marginTop: 6,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        gap: MEDIA_GAP,
                      }}
                    >
                      <Image.PreviewGroup>
                        {it.media.map((m) => (
                          <MediaBox key={m.url}>
                            {m.kind === 'video' ? (
                              <VideoTile
                                src={m.url}
                                cover={m.cover}
                                onPlay={setPlaying}
                              />
                            ) : (
                              <Image
                                src={m.url}
                                width={MEDIA_SIZE}
                                height={MEDIA_SIZE}
                                style={{ objectFit: 'cover' }}
                              />
                            )}
                          </MediaBox>
                        ))}
                      </Image.PreviewGroup>
                    </div>
                  )}
                  {it.bodyText && (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 12,
                        color: 'rgba(0,0,0,.65)',
                        wordBreak: 'break-all',
                      }}
                    >
                      {it.bodyText}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}
      <Modal
        title="打卡视频"
        open={!!playing}
        footer={null}
        width={680}
        centered
        destroyOnClose
        onCancel={() => setPlaying(null)}
      >
        {playing && (
          <div>
            <video
              src={playing}
              controls
              autoPlay
              playsInline
              style={{
                width: '100%',
                maxHeight: '64vh',
                background: '#000',
                borderRadius: MEDIA_RADIUS,
                display: 'block',
              }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              播不出来？
              <a href={playing} target="_blank" rel="noreferrer">
                在新窗口打开
              </a>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

DetailGroups.propTypes = {
  groups: PropTypes.array,
};

DetailGroups.defaultProps = {
  groups: [],
};

const CheckInOverview = ({ userId }) => {
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState(null); // { reason, code, msg }
  const [overview, setOverview] = useState(null);
  const [heatmap, setHeatmap] = useState([]);

  const [detailDate, setDetailDate] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailGroups, setDetailGroups] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailOfflineDegraded, setDetailOfflineDegraded] = useState(false);
  /** 后端媒体回捞（图/视频/备注）是否完整；false = 部分图缺失，不等于该打卡没图 */
  const [mediaOk, setMediaOk] = useState(true);
  /** 概览请求序号：重试引入额外 await 后，用来丢弃过期响应 */
  const loadSeqRef = useRef(0);

  const load = useCallback(async () => {
    if (!userId) return;
    // 重试会多一个 await，期间可能换账号/卸载；用序号丢弃过期响应，避免旧请求覆盖新页面
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setFailure(null);
    let res = await getSignOverview({ userId, weeks: 52 });
    /**
     * 训练子类型（真人/入镜/其它）是后端逐日补查拼出来的，冷启动很容易超预算而整块缺失
     * （trainingSplitAvailable=false）。但首次请求已经把部分天写进了后端缓存，
     * 隔一会儿再拉一次基本就能补齐 → 静默重试一次，让用户直接看到拆分，
     * 而不是先看到「拆分暂不可用」再变。只重试一次，不循环。
     */
    if (
      res.ok &&
      Number(res.data?.overview?.trainingCount || 0) > 0 &&
      !hasUsableTrainingSplit(res.data?.overview)
    ) {
      await wait(SPLIT_RETRY_DELAY_MS);
      if (seq !== loadSeqRef.current) return;
      const retry = await getSignOverview({ userId, weeks: 52 });
      if (retry.ok) res = retry;
    }
    if (seq !== loadSeqRef.current) return;
    setLoading(false);
    if (!res.ok) {
      setFailure(res);
      setOverview(null);
      setHeatmap([]);
      return;
    }
    setOverview(res.data.overview || null);
    setHeatmap(Array.isArray(res.data.heatmap) ? res.data.heatmap : []);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const weeks = useMemo(() => toHeatmapWeeks(heatmap), [heatmap]);

  // 图上口径：有打卡的天数 / 总次数，与热力图同源，避免卡片和图对不上
  const dayCount = useMemo(
    () => weeks.reduce((s, w) => s + w.days.length, 0),
    [weeks],
  );

  /**
   * 最近一次打卡日期（PRD：统计范围内最近一条打卡的发生时间，线下碰一碰也算一次）。
   * 直接取热力图日格：totalCount 已含碰一碰（后端归并进到店那一格），
   * 所以“最后一个有打卡的格子”就是答案，不用再请求后端。
   * 粒度到天，卡片也只展示 MM-DD，与设稿一致。
   */
  const lastSignDay = useMemo(() => {
    const hit = (heatmap || [])
      .filter(
        (c) =>
          c && c.date && (c.hasSign === true || Number(c.totalCount || 0) > 0),
      )
      .map((c) => c.date)
      .sort();
    return hit.length ? hit[hit.length - 1] : null;
  }, [heatmap]);

  const openDetail = useCallback(
    async (day) => {
      if (!day || !day.date) return;
      setDetailDate(day.date);
      setDetailLoading(true);
      setDetailGroups([]);
      setMediaOk(true);
      setDetailTotal(0);
      setDetailOfflineDegraded(false);
      setMediaOk(true);
      const res = await getSignDailyDetail({ userId, date: day.date });
      setDetailLoading(false);
      if (!res.ok) {
        message.error(
          FAILURE_COPY[res.reason] || res.msg || '当天明细加载失败',
        );
        return;
      }
      setDetailGroups(toSignDetailViews(res.data.groups));
      // 后端按 signId 回捞媒体超时/饱和时置 false：图缺失不等于没图，得告知教练
      setMediaOk(res.data.mediaAvailable !== false);
      setDetailTotal(Number(res.data.total || 0));
      // 后端只会在「到店这一路真的拉失败」时置 false，其余四类照常返回
      setDetailOfflineDegraded(res.data.offlineAvailable === false);
    },
    [userId],
  );

  if (!userId) return null;

  if (loading) {
    return (
      <div style={{ marginTop: 16, padding: '48px 0', textAlign: 'center' }}>
        <Spin tip="打卡数据加载中…" />
      </div>
    );
  }

  if (failure) {
    return (
      <div style={{ marginTop: 16 }}>
        <Alert
          type="error"
          showIcon
          message="打卡数据加载失败"
          description={`${FAILURE_COPY[failure.reason] || '未知错误'}（code: ${
            failure.code ?? '—'
          }）`}
          action={
            <a onClick={load} style={{ fontSize: 13 }}>
              重试
            </a>
          }
        />
      </div>
    );
  }

  const o = overview || {};
  const offlineDegraded = o.offlineAvailable === false;
  const splitUsable = hasUsableTrainingSplit(o);

  // 打卡数 = 卡上列出的这几类之和（同源，保证副标题相加一定等于大数字）
  const breakdown = getSignBreakdown(o);
  const signTotal = breakdown.reduce((s, b) => s + b.value, 0);
  const backendTotal = Number(o.totalSignCount || 0);
  if (backendTotal !== signTotal) {
    // 后端 totalSignCount 与分类合计不等 = 口径漂移，不静默吞掉
    // eslint-disable-next-line no-console
    console.warn(
      '[打卡概览] totalSignCount 与五类合计不一致：',
      backendTotal,
      '!=',
      signTotal,
    );
  }
  const breakdownSub = breakdown
    .map((b) => `${b.shortLabel} ${b.value}`)
    .join('； ');

  // 拆分可信就拆，不可信就只报合并总数（不拿半截数据拆）
  const trainingSub = splitUsable
    ? `真人指导：${dashIfNull(o.trainingLiveCount)}； 入镜指导：${dashIfNull(
        o.trainingMirrorCount,
      )}； 自由训练：${dashIfNull(o.trainingOtherCount)}`
    : `训练打卡：${o.trainingCount ?? 0} 次${
        o.trainingSplitAvailable === false ? '（拆分暂不可用）' : ''
      }`;

  return (
    <div style={{ marginTop: 16 }}>
      {offlineDegraded && (
        <Alert
          style={{ marginBottom: 12 }}
          type="warning"
          showIcon
          message="到店数据暂时拉不到，其余四类正常"
          description="本页面上的到店次数、到店天数、最近一次打卡时间会偏低，恢复后重进页面即可。"
        />
      )}
      <div style={{ fontSize: 14, color: 'rgba(0,0,0,.85)', marginBottom: 12 }}>
        近52周数据概览
        {o.startDate && (
          <span style={{ fontSize: 12, color: '#bfbfbf', marginLeft: 8 }}>
            {o.startDate} ~ {o.endDate}
          </span>
        )}
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={
              <IconBadge>
                <ClockCircleOutlined />
              </IconBadge>
            }
            main={`总完课数：${o.totalClassCount ?? 0}`}
            sub={trainingSub}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={
              <IconBadge bg="#f6ffed">
                <AppstoreOutlined />
              </IconBadge>
            }
            main={`打卡数：${signTotal}`}
            sub={breakdownSub}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={
              <IconBadge bg="#f9f0ff" color="#722ed1">
                <HomeOutlined />
              </IconBadge>
            }
            main={o.offlineDayCount ?? 0}
            sub="近52周到店天数"
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={
              <IconBadge bg="#e6f7ff" color="#1890ff">
                <RiseOutlined />
              </IconBadge>
            }
            main={lastSignDay ? moment(lastSignDay).format('MM-DD') : '—'}
            sub="最近一次打卡时间"
            big={24}
          />
        </Col>
      </Row>

      <Card
        bordered={false}
        style={{ marginTop: 16, background: '#fafafa' }}
        bodyStyle={{ padding: '20px 24px' }}
      >
        <div
          style={{ marginBottom: 16, fontSize: 14, color: 'rgba(0,0,0,.85)' }}
        >
          近52周打卡热力图
          <span style={{ fontSize: 12, color: '#bfbfbf', marginLeft: 8 }}>
            有打卡 {dayCount} 天 / 共 {signTotal} 次
          </span>
        </div>

        {signTotal === 0 && dayCount === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="近52周没有打卡记录"
          />
        ) : (
          <CalendarHeatmap
            weeks={weeks}
            types={HEATMAP_TYPES}
            autoFit
            mode="stack"
            /**
             * 宽屏上把格子尺寸上限抬到 38、间隙上限抬到 10（尺寸口径由业务定）。
             * 默认 26/8 在 2400px 屏上会提前顶到上限，多出 546px 只能左右留空；
             * 38/10 实测：格子 26→38px，图高 268→336px，右侧空量 546→28px。
             * 窄屏（≤1800）不受这个上限约束：边长由容器宽反推，不会变大。
             */
            maxCell={38}
            maxCellGap={10}
            rangeStart={o.startDate}
            rangeEnd={o.endDate}
            onDateClick={openDetail}
          />
        )}

        <div style={{ fontSize: 12, color: '#bfbfbf', marginTop: 8 }}>
          悬停查看摘要 · 点击日期查看当天详情
        </div>
      </Card>

      <Modal
        title={detailDate ? `${detailDate} 打卡明细` : ''}
        open={!!detailDate}
        onCancel={() => setDetailDate(null)}
        footer={null}
        width={640}
      >
        {detailLoading ? (
          <div style={{ padding: '32px 0', textAlign: 'center' }}>
            <Spin />
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
              共 {detailTotal} 次
            </div>
            {detailOfflineDegraded && (
              <Alert
                style={{ marginBottom: 12 }}
                type="warning"
                showIcon
                message="当天到店记录可能不完整（到店服务暂时不可用）"
              />
            )}
            {!mediaOk && (
              <Alert
                style={{ marginBottom: 12 }}
                type="warning"
                showIcon
                message="部分打卡图片/视频暂时加载失败，请稍后重试"
              />
            )}
            <DetailGroups groups={detailGroups} />
          </>
        )}
      </Modal>
    </div>
  );
};

CheckInOverview.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

CheckInOverview.defaultProps = {
  userId: null,
};

export default CheckInOverview;
